import { MdlDialogReference } from '@angular-mdl/core';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, InjectionToken } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonLinkInfo } from './common-link-info';
import { CommonLinkEditorOptions } from './common-links-editor';
import { maxLengthInBytes, maxBytesError } from '../../shared/validators';

export const COMMON_LINK_EDITOR_OPTIONS = new InjectionToken('EditorCommonLinkOptions');

export enum CommonLinkEditorResultAction {
    close,
    create,
    save
}

export interface CommonLinkEditorResult {
    action: CommonLinkEditorResultAction;
    editedEntity?: CommonLinkInfo;
}

@Component({
    selector: 'common-link-editor',
    template: require('./common-link-editor.component.html'),
    styles: [require('./common-link-editor.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommonLinkEditorComponent implements AfterViewInit {
    public readonly invalidUrlValidationName = 'invalidUrl';

    public readonly editorTitle: string;
    public readonly actionButtonTitle: string;

    public readonly rolesControl: FormControl;
    public readonly titleControl: FormControl;
    public readonly urlControl: FormControl;
    public readonly descriptionControl: FormControl;
    public readonly sortOrderControl: FormControl;
    public readonly editorForm: FormGroup;

    private readonly titleMaxBytes = 75;
    private readonly urlMaxBytes = 255;
    private readonly descriptionMaxBytes = 255;
    private readonly sortOrderPattern = '[0-9]+?';

    constructor(
        private readonly dialog: MdlDialogReference,
        private ref: ChangeDetectorRef,
        @Inject(COMMON_LINK_EDITOR_OPTIONS) private readonly editorOptions: CommonLinkEditorOptions,
        fb: FormBuilder
    ) {
        const { isNew, editedEntity } = this.editorOptions;

        this.editorTitle = isNew ? 'New Common Link' : 'Edit Common Link';
        this.actionButtonTitle = isNew ? 'Create' : 'Save';

        this.rolesControl = fb.control(editedEntity.roles, [Validators.required]);
        this.titleControl = fb.control(editedEntity.title, [Validators.required, maxLengthInBytes(this.titleMaxBytes)]);
        this.urlControl = fb.control(editedEntity.url, [Validators.required, maxLengthInBytes(this.urlMaxBytes), this.urlValidator()]);
        this.descriptionControl = fb.control(editedEntity.description, [maxLengthInBytes(this.descriptionMaxBytes)]);
        this.sortOrderControl = fb.control(editedEntity.sortOrder, [Validators.pattern(this.sortOrderPattern)]);
        this.editorForm = fb.group({
            'roles': this.rolesControl,
            'title': this.titleControl,
            'url': this.urlControl,
            'description': this.descriptionControl,
            'sortOrder': this.sortOrderControl
        });
    }

    public get titleErrorMessage(): string {
        if (this.titleControl.hasError('required')) {
            return 'Title is required';
        }

        if (this.titleControl.hasError(maxBytesError)) {
            return 'Title is too long';
        }

        return '';
    }

    public get urlErrorMessage(): string {
        if (this.urlControl.hasError('required')) {
            return 'Url is required';
        }

        if (this.urlControl.hasError(maxBytesError)) {
            return 'Url is too long';
        }

        if (this.urlControl.hasError(this.invalidUrlValidationName)) {
            return 'Must be a valid url (e.g. http://example.com)';
        }

        return '';
    }

    public get descriptionErrorMessage(): string {
        return this.descriptionControl.hasError(maxBytesError)
            ? 'Description is too long'
            : '';
    }

    public get sortOrderErrorMessage(): string {
        if (this.sortOrderControl.hasError('pattern')) {
            return `Sort order must be a number`;
        }
        return '';
    }

    public ngAfterViewInit() {
        //TODO: Find reason why floating labels don't work with OnPush strategy
        this.ref.markForCheck();
    }

    public onCloseEditor() {
        const result: CommonLinkEditorResult = {
            action: CommonLinkEditorResultAction.close
        };
        this.dialog.hide(result);
    }

    public onClickAction() {
        if (this.editorOptions.isNew) {
            this.createCommonLink();
        } else {
            this.saveCommonLink();
        }
    }

    public createCommonLink() {
        const newCommonLink = new CommonLinkInfo();

        this.assignValues(newCommonLink);

        const result: CommonLinkEditorResult = {
            action: CommonLinkEditorResultAction.create,
            editedEntity: newCommonLink
        };

        this.dialog.hide(result);
    }

    public saveCommonLink() {
        const { editedEntity } = this.editorOptions;

        this.assignValues(editedEntity);

        const result: CommonLinkEditorResult = {
            action: CommonLinkEditorResultAction.save,
            editedEntity: editedEntity
        };

        this.dialog.hide(result);
    }

    private assignValues(commonLink: CommonLinkInfo) {
        commonLink.roles = this.rolesControl.value;
        commonLink.title = this.titleControl.value;
        commonLink.url = this.urlControl.value;
        commonLink.description = this.descriptionControl.value;
        commonLink.sortOrder = this.sortOrderControl.value;
    }

    private urlValidator() {
        const isValidUrl = /^[^\s/$.?#]+:\/\/[^\s/$.?#].[^\s]*$/i;

        return (control: AbstractControl): { [key: string]: any } | null => {
            return !isValidUrl.test(control.value) ? { [this.invalidUrlValidationName]: { value: control.value } } : null;
        };
    }
}
